/* eslint-disable @typescript-eslint/no-restricted-types */
/* eslint-disable no-console */
/**
 * Storage utilities for auth data with encryption support
 */

/**
 * Secure storage interface for sensitive auth data
 */
export interface SecureStorage {
  setItem(key: string, value: string): Promise<void>;
  getItem(key: string): Promise<string | null>;
  removeItem(key: string): void;
  clear(): void;
}

/**
 * Simple encryption/decryption using Web Crypto API
 * Note: This is for basic obfuscation, not military-grade security
 */
class SimpleEncryption {
  private key: CryptoKey | null = null;

  async init(): Promise<void> {
    if (this.key) {
      return;
    }

    try {
      // Generate a key for AES-GCM encryption
      this.key = await window.crypto.subtle.generateKey(
        {
          name: 'AES-GCM',
          length: 256,
        },
        false,
        ['encrypt', 'decrypt'],
      );
    } catch {
      console.warn('Web Crypto API not available, falling back to base64 encoding');
    }
  }

  async encrypt(text: string): Promise<string> {
    await this.init();

    if (!this.key) {
      // Fallback to base64 encoding if crypto is not available
      return btoa(text);
    }

    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(text);

      // Generate a random IV
      const iv = window.crypto.getRandomValues(new Uint8Array(12));

      const encrypted = await window.crypto.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv,
        },
        this.key,
        data,
      );

      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encrypted.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encrypted), iv.length);

      // Convert to base64
      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      console.warn('Encryption failed, falling back to base64:', error);
      return btoa(text);
    }
  }

  async decrypt(encryptedText: string): Promise<string> {
    await this.init();

    if (!this.key) {
      // Fallback to base64 decoding if crypto is not available
      try {
        return atob(encryptedText);
      } catch {
        return encryptedText; // Return as-is if not base64
      }
    }

    try {
      // Convert from base64
      const combined = new Uint8Array(
        atob(encryptedText)
          .split('')
          .map(char => char.charCodeAt(0)),
      );

      // Extract IV and encrypted data
      const iv = combined.slice(0, 12);
      const encrypted = combined.slice(12);

      const decrypted = await window.crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv,
        },
        this.key,
        encrypted,
      );

      const decoder = new TextDecoder();
      return decoder.decode(decrypted);
    } catch (error) {
      console.warn('Decryption failed, trying base64 fallback:', error);
      try {
        return atob(encryptedText);
      } catch {
        return encryptedText; // Return as-is if all else fails
      }
    }
  }
}

/**
 * Secure localStorage implementation with encryption
 */
class SecureLocalStorage implements SecureStorage {
  private readonly encryption = new SimpleEncryption();
  private get keyPrefix() {
    return 'secure_';
  }

  constructor(private readonly namespace = 'okta_auth') {}

  async setItem(key: string, value: string): Promise<void> {
    try {
      const encrypted = await this.encryption.encrypt(value);
      localStorage.setItem(this.getStorageKey(key), encrypted);
    } catch (error) {
      console.error('Failed to set secure item:', error);
      // Fallback to unencrypted storage
      localStorage.setItem(this.getStorageKey(key), value);
    }
  }

  async getItem(key: string): Promise<string | null> {
    try {
      const encrypted = localStorage.getItem(this.getStorageKey(key));
      if (!encrypted) {
        return null;
      }

      return await this.encryption.decrypt(encrypted);
    } catch (error) {
      console.error('Failed to get secure item:', error);
      // Fallback to direct retrieval
      return localStorage.getItem(this.getStorageKey(key));
    }
  }

  removeItem(key: string): void {
    localStorage.removeItem(this.getStorageKey(key));
  }

  clear(): void {
    // Clear only items with our prefix
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.keyPrefix + this.namespace)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
  }

  private getStorageKey(key: string): string {
    return `${this.keyPrefix}${this.namespace}_${key}`;
  }
}

/**
 * Session data manager with automatic cleanup
 */
export class SessionManager {
  private readonly storage: SecureLocalStorage;
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor(namespace = 'okta_auth') {
    this.storage = new SecureLocalStorage(namespace);
    this.startCleanupTimer();
  }

  /**
   * Stop cleanup timer (call on unmount)
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  /**
   * Store session data with expiration
   */
  async setSessionData(key: string, data: unknown, expiresInMinutes = 60): Promise<void> {
    const sessionData = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + expiresInMinutes * 60 * 1000,
    };

    await this.storage.setItem(key, JSON.stringify(sessionData));
  }

  /**
   * Retrieve session data if not expired
   */
  async getSessionData<T>(key: string): Promise<T | null> {
    try {
      const stored = await this.storage.getItem(key);
      if (!stored) {
        return null;
      }

      const sessionData = JSON.parse(stored);

      // Check if expired
      if (Date.now() > sessionData.expiresAt) {
        this.storage.removeItem(key);
        return null;
      }

      return sessionData.data as T;
    } catch (error) {
      console.error('Failed to retrieve session data:', error);
      return null;
    }
  }

  /**
   * Remove session data
   */
  removeSessionData(key: string): void {
    this.storage.removeItem(key);
  }

  /**
   * Clear all session data
   */
  clearAllSessionData(): void {
    this.storage.clear();
  }

  /**
   * Start automatic cleanup of expired items
   */
  private startCleanupTimer(): void {
    // Clean up expired items every 5 minutes
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredItems();
    }, 5 * 60 * 1000);
  }

  /**
   * Clean up expired session items
   */
  private async cleanupExpiredItems(): Promise<void> {
    try {
      const keysToCheck: string[] = [];

      // Find all keys with our prefix
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('secure_okta_auth_')) {
          keysToCheck.push(key.replace('secure_okta_auth_', ''));
        }
      }

      // Check each key for expiration
      for (const key of keysToCheck) {
        // eslint-disable-next-line no-await-in-loop
        await this.getSessionData(key);
        // getSessionData already removes expired items
      }
    } catch (error) {
      console.error('Error during session cleanup:', error);
    }
  }
}

/**
 * User preferences manager
 */
export class UserPreferencesManager {
  private readonly sessionManager: SessionManager;

  constructor() {
    this.sessionManager = new SessionManager('user_prefs');
  }

  async setPreference(key: string, value: unknown): Promise<void> {
    // User preferences don't expire
    await this.sessionManager.setSessionData(key, value, 365 * 24 * 60); // 1 year
  }

  async getPreference<T>(key: string, defaultValue?: T): Promise<T | undefined> {
    const value = await this.sessionManager.getSessionData<T>(key);
    return value ?? defaultValue;
  }

  removePreference(key: string): void {
    this.sessionManager.removeSessionData(key);
  }

  clearAllPreferences(): void {
    this.sessionManager.clearAllSessionData();
  }
}

// Export singleton instances
export const sessionManager = new SessionManager();
export const userPreferencesManager = new UserPreferencesManager();

/**
 * Auth storage keys
 */
export const AUTH_STORAGE_KEYS = {
  LAST_LOGIN: 'last_login',
  LOGIN_ATTEMPTS: 'login_attempts',
  SESSION_METADATA: 'session_metadata',
  ERROR_LOG: 'error_log',
  USER_ACTIVITY: 'user_activity',
} as const;
